import type { WorkflowReminder } from "./models";

const en = {
    summaryReady: "Project summary ready",
    handoverReady: "Shift handover ready",
    handoverFailed: "Shift handover could not be generated",
    handoverReadyMessage: "Your shift briefing is ready in Workspace.",
    handoverUpdated: "Shift schedule updated",
    handoverUpdatedMessage: "An updated shift briefing is ready in Workspace.",
    handoverRemovedMessage: "A previously notified shift is no longer assigned to you. Review the update in Workspace.",
    openHandover: "Open handover",
    reportReady: "Report ready",
    reportReadyMessage: "Your scheduled Word report is ready in Workspace.",
    openReport: "Open report",
    reportFailed: "Report could not be generated",
    summaryReadyMessage: "Your scheduled summary is ready in Workspace.",
    openSummary: "Open summary",
    summaryFailed: "Project summary could not be generated",
    summaryFailedMessage: "Open Workspace to review the scheduled run.",
    openFailedSummary: "View run",
    reminders: "Reminders",
    reminder: "Reminder",
    dismiss: "Dismiss reminder",
    dismissFailed: "This reminder could not be dismissed. Please try again.",
    retry: "Try again"
};

type ReminderStrings = typeof en;

const translations: Record<string, ReminderStrings> = {
    en,
    sv: {
        handoverReady: "Överlämningen är klar",
        handoverFailed: "Överlämningen kunde inte skapas",
        handoverReadyMessage: "Din sammanfattning inför passet finns i Workspace.",
        handoverUpdated: "Arbetsschemat har uppdaterats",
        handoverUpdatedMessage: "En uppdaterad sammanfattning inför passet finns i Workspace.",
        handoverRemovedMessage: "Ett tidigare aviserat pass är inte längre tilldelat dig. Läs uppdateringen i Workspace.",
        openHandover: "Öppna överlämning",
        summaryReady: "Projektsammanfattningen är klar",
        reportReady: "Rapporten är klar",
        reportReadyMessage: "Din schemalagda Word-rapport finns i Workspace.",
        openReport: "Öppna rapport",
        reportFailed: "Rapporten kunde inte skapas",
        summaryReadyMessage: "Din schemalagda sammanfattning finns i Workspace.",
        openSummary: "Öppna sammanfattning",
        summaryFailed: "Projektsammanfattningen kunde inte skapas",
        summaryFailedMessage: "Öppna Workspace för att granska den schemalagda körningen.",
        openFailedSummary: "Visa körning",
        reminders: "Påminnelser",
        reminder: "Påminnelse",
        dismiss: "Stäng påminnelsen",
        dismissFailed: "Påminnelsen kunde inte stängas. Försök igen.",
        retry: "Försök igen"
    },
    de: {
        handoverReady: "Schichtübergabe bereit",
        handoverFailed: "Schichtübergabe konnte nicht erstellt werden",
        handoverReadyMessage: "Ihre Schichtübersicht ist in Workspace verfügbar.",
        handoverUpdated: "Schichtplan aktualisiert",
        handoverUpdatedMessage: "Eine aktualisierte Schichtübersicht ist in Workspace verfügbar.",
        handoverRemovedMessage: "Eine zuvor angekündigte Schicht ist Ihnen nicht mehr zugewiesen. Prüfen Sie die Änderung in Workspace.",
        openHandover: "Schichtübergabe öffnen",
        summaryReady: "Projektzusammenfassung bereit",
        reportReady: "Bericht bereit",
        reportReadyMessage: "Ihr geplanter Word-Bericht ist in Workspace verfügbar.",
        openReport: "Bericht öffnen",
        reportFailed: "Bericht konnte nicht erstellt werden",
        summaryReadyMessage: "Ihre geplante Zusammenfassung ist in Workspace verfügbar.",
        openSummary: "Zusammenfassung öffnen",
        summaryFailed: "Projektzusammenfassung konnte nicht erstellt werden",
        summaryFailedMessage: "Öffnen Sie Workspace, um den geplanten Lauf zu prüfen.",
        openFailedSummary: "Lauf ansehen",
        reminders: "Erinnerungen",
        reminder: "Erinnerung",
        dismiss: "Erinnerung schließen",
        dismissFailed: "Die Erinnerung konnte nicht geschlossen werden. Bitte versuche es erneut.",
        retry: "Erneut versuchen"
    },
    zh: {
        handoverReady: "班前交接已就绪",
        handoverFailed: "班前交接未能生成",
        handoverReadyMessage: "本次班前简报已保存到 Workspace。",
        handoverUpdated: "排班已更新",
        handoverUpdatedMessage: "更新版班前交接简报已保存到 Workspace。",
        handoverRemovedMessage: "此前通知的班次已不再分配给你，请在 Workspace 中查看更新。",
        openHandover: "查看交接简报",
        summaryReady: "项目总结已完成",
        reportReady: "报告已完成",
        reportReadyMessage: "定时 Word 报告已保存到 Workspace。",
        openReport: "查看报告",
        reportFailed: "报告未能生成",
        summaryReadyMessage: "定时总结已保存到 Workspace。",
        openSummary: "查看总结",
        summaryFailed: "项目总结未能生成",
        summaryFailedMessage: "请在 Workspace 中查看本次定时运行。",
        openFailedSummary: "查看运行记录",
        reminders: "提醒",
        reminder: "提醒",
        dismiss: "关闭提醒",
        dismissFailed: "未能关闭这条提醒，请重试。",
        retry: "重试"
    }
};

export function reminderToastStrings(language: string): ReminderStrings {
    return translations[language.split("-")[0]] ?? en;
}

/** Summary titles and bodies remain behind Workspace's current authorization checks. */
export function reminderNoticeText(reminder: WorkflowReminder, language: string): { title: string; message: string } {
    const text = reminderToastStrings(language);
    if (!reminder.result_id) return { title: reminder.title || text.reminder, message: reminder.message };
    if (reminder.result_kind === "shift_handover") {
        if (reminder.result_status === "failed") return { title: text.handoverFailed, message: text.summaryFailedMessage };
        if (reminder.change_kind === "updated") return { title: text.handoverUpdated, message: text.handoverUpdatedMessage };
        if (reminder.change_kind === "removed") return { title: text.handoverUpdated, message: text.handoverRemovedMessage };
        return { title: text.handoverReady, message: text.handoverReadyMessage };
    }
    if (reminder.result_format === "docx") return reminder.result_status === "failed"
        ? { title: text.reportFailed, message: text.summaryFailedMessage }
        : { title: text.reportReady, message: text.reportReadyMessage };
    return reminder.result_status === "failed"
        ? { title: text.summaryFailed, message: text.summaryFailedMessage }
        : { title: text.summaryReady, message: text.summaryReadyMessage };
}
